import { Injectable } from '@angular/core';
import { ProductSubtypeEnum } from '@app/models/enums/product-subtype.enum';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { ProductServiceInterface } from '@app/models/interfaces/product-service.interface';
import { SearchParamsInterface } from '@app/models/interfaces/search-params.interface';
import { SearchResultsInterface } from '@app/models/interfaces/search-results.interface';
import { TaqmanSearchRequestInterface } from '@app/models/requests/taqman-search-request.interface';
import { TaqmanSearchResponseInterface } from '@app/models/responses/taqman-search-response.interface';
import { CNVProductService } from '@app/products/cnv/services/cnv-product.service';
import { GXProductService } from '@app/products/ge/arrays/services/gx-product.service';
import { GEProductService } from '@app/products/ge/assays/services/ge-product.service';
import { MicrornaAdvancedProductService } from '@app/products/microrna/microrna-advanced/services/microrna-advanced-product.service';
import { MicrornaArrayProductService } from '@app/products/microrna/microrna-array/services/microrna-array-product.service';
import { MicrornaClassicProductService } from '@app/products/microrna/microrna-classic/services/microrna-classic-product.service';
import { MicrornaPrimaryProductService } from '@app/products/microrna/microrna-primary/services/microrna-primary-product.service';
import { SNPProductService } from '@app/products/snp/services/snp-product.service';
import { TfApiService } from '@app/services/api.service';
import { ProductSiteRegionService } from './product-site-region.service';
import { map, Observable } from 'rxjs';

@Injectable({providedIn: 'root'})
export class SearchService {

    productMappers: Record<ProductSubtypeEnum, ProductServiceInterface> = {
        [ProductSubtypeEnum.ge]: undefined,
        [ProductSubtypeEnum.gx]: undefined,
        [ProductSubtypeEnum.snp]: undefined,
        [ProductSubtypeEnum.cnv]: undefined,
        [ProductSubtypeEnum.microbre_all]: undefined,
        [ProductSubtypeEnum.microrna_classic]: undefined,
        [ProductSubtypeEnum.microrna_advanced]: undefined,
        [ProductSubtypeEnum.microrna_primary]: undefined,
        [ProductSubtypeEnum.microrna_all]: undefined,
        [ProductSubtypeEnum.microrna_array]: undefined,
    };

    constructor(
        private readonly apiService: TfApiService,
        cnvProductService: CNVProductService,
        geProductService: GEProductService,
        gxProductService: GXProductService,
        snpProductService: SNPProductService,
        micrornaClassicProductService: MicrornaClassicProductService,
        micrornaAdvancedProductService: MicrornaAdvancedProductService,
        micrornaPrimaryProductService: MicrornaPrimaryProductService,
        micrornaArrayProductService: MicrornaArrayProductService,
        private readonly productSiteRegionService: ProductSiteRegionService,
    ) {

        this.productMappers[ProductSubtypeEnum.ge] = geProductService;
        this.productMappers[ProductSubtypeEnum.gx] = gxProductService;
        this.productMappers[ProductSubtypeEnum.snp] = snpProductService;
        this.productMappers[ProductSubtypeEnum.cnv] = cnvProductService;
        this.productMappers[ProductSubtypeEnum.microrna_classic] = micrornaClassicProductService;
        this.productMappers[ProductSubtypeEnum.microrna_advanced] = micrornaAdvancedProductService;
        this.productMappers[ProductSubtypeEnum.microrna_primary] = micrornaPrimaryProductService;
        this.productMappers[ProductSubtypeEnum.microrna_array] = micrornaArrayProductService;

    }

    doSearch(searchParams: SearchParamsInterface, filterState: any): Observable<SearchResultsInterface> {

        const taqmanSearchParams: TaqmanSearchRequestInterface = {
            keyword: searchParams.keyword,
            offset: searchParams.offset,
            productType: searchParams.productType,
            productSubtype: searchParams.productSubtype,
            sortBy: searchParams.sortBy,
            filters: searchParams.filters,
            sortOrder: searchParams.sortOrder,
            filterState: filterState
        }

        if (this.productSiteRegionService.isFisherSci() === true) {
            taqmanSearchParams.isFSCI = true;
        }

        if (searchParams.limit) {
            taqmanSearchParams.limit = searchParams.limit;
        }

        if (searchParams.broadSearch === true) {
            taqmanSearchParams.broadsearch = true;
        }

        return this.apiService.search(taqmanSearchParams).pipe(
            map((response) => this.processSearchResult(taqmanSearchParams, response))
        );

    }

    private processSearchResult(searchParams: TaqmanSearchRequestInterface, response: TaqmanSearchResponseInterface): SearchResultsInterface {

        this.processFilters(response.filters);

        const productMapper = this.productMappers[searchParams.productSubtype];

        const products = productMapper.makeProductFromSearchResult(response);

        const newResults: SearchResultsInterface = {
            filters: response.filters,
            keyword: searchParams.keyword,
            productType: searchParams.productType,
            productSubType: searchParams.productSubtype,
            productTypeEnum: ProductTypeEnum[searchParams.productType],
            products: {},
            totalHits: response.totalHits,
            keywordUsed: response.keyword,
            suggestedKeywords: response.suggestedKeywords,
            packageObjs: response.packageObjs,
            suggestedKeywordsArray: this.parseSuggestedKeywords(response.suggestedKeywords)
        };

        switch (searchParams.productSubtype) {

            case ProductSubtypeEnum.ge:
                newResults.products[ProductSubtypeEnum.ge] = products;
                break;

            case ProductSubtypeEnum.gx:
                newResults.products[ProductSubtypeEnum.gx] = products;
                break;

            case ProductSubtypeEnum.snp:
                this.processSNPCodeBrakerFlag(JSON.parse(response.packageObjs), products);
                newResults.products[ProductSubtypeEnum.snp] = products;
                break;

            case ProductSubtypeEnum.cnv:
                newResults.products[ProductSubtypeEnum.cnv] = products;
                break;

            case ProductSubtypeEnum.microrna_advanced:
                newResults.products[ProductSubtypeEnum.microrna_advanced] = products;
                break;

            case ProductSubtypeEnum.microrna_classic:
                newResults.products[ProductSubtypeEnum.microrna_classic] = products;
                break;

            case ProductSubtypeEnum.microrna_primary:
                newResults.products[ProductSubtypeEnum.microrna_primary] = products;
                break;

            case ProductSubtypeEnum.microrna_array:
                newResults.products[ProductSubtypeEnum.microrna_array] = products;
                break;

        }

        return newResults;

    }

    private processSNPCodeBrakerFlag(packageData, products) {
        // Get Flags
        const flags = {};
        if(packageData && packageData[0]) {
            if (packageData[0]['DMEInv1'] && packageData[0]['DMEInv1'].skuList && packageData[0]['DMEInv1'].skuList[0] && packageData[0]['DMEInv1'].skuList[0].skuDetails) {
                const skuList = packageData[0]['DMEInv1'].skuList[0].skuDetails;
                skuList.forEach(sku => {
                    flags[sku.skuNumber.value] = sku.displayOnSearch;
                });
            }
        }

        // Assign flags to products skus
        for (const product of products) {
            for (const sku of product.dyeSizeData) {
                if(product.packageId === "DMEInv1") {
                    sku.displayOnSearch = !(flags[sku.catalogNumber] === 'N');
                } else {
                    sku.displayOnSearch = true;
                }
            }
        }
    }

    private processFilters(filters) {
        filters?.forEach(filter => {
            filter.navigators.forEach(navigator => {
                if (navigator.name === 'openarray') {
                    navigator.name = 'OpenArray\u2122';
                }
            })
        })
    }

    private parseSuggestedKeywords(suggestedKeywords: string) {
        let parsed: string[] = [];
        if (suggestedKeywords && suggestedKeywords.length > 0) {
            parsed = suggestedKeywords.split(',');
        }
        return parsed;
    }

}
